import React, { useState } from 'react';
import { ConfigDocument, SMBDestination, TestResult } from '../../types';
import { testSMBConnection } from '../../services/configApi';

interface SMBDestinationsTabProps {
  config: ConfigDocument | null;
  onSave: (config: ConfigDocument) => Promise<void>;
  loading: boolean;
}

interface FormData {
  name: string;
  uncPath: string;
  stagingPath: string;
  hotfolderPath: string;
  credentialSetName: string;
}

interface ValidationErrors {
  name?: string;
  uncPath?: string;
  stagingPath?: string;
  hotfolderPath?: string;
  credentialSetName?: string;
}

interface ConnectionTestState {
  loading: boolean;
  result: TestResult | null;
}

const EMPTY_FORM: FormData = { name: '', uncPath: '', stagingPath: '', hotfolderPath: '', credentialSetName: '' };

const SMBDestinationsTab: React.FC<SMBDestinationsTabProps> = ({ config, onSave, loading }) => {
  const [editing, setEditing] = useState<string | null>(null); // key of destination being edited, or 'new'
  const [formData, setFormData] = useState<FormData>({ ...EMPTY_FORM });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [saving, setSaving] = useState(false);
  const [testStates, setTestStates] = useState<Record<string, ConnectionTestState>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Get credential set names from config
  const credentialSetNames = config ? Object.keys(config.credentialSets).sort((a, b) => a.localeCompare(b)) : [];

  // Get destinations as array of [key, value]
  const destinations: [string, SMBDestination][] = config
    ? Object.entries(config.smbDestinations).sort(([a], [b]) => a.localeCompare(b))
    : [];

  // Find presses using a given SMB destination key
  const getPressesUsingDestination = (destKey: string): string[] => {
    if (!config) return [];
    return Object.entries(config.presses)
      .filter(([, press]) => press.smbDestination === destKey)
      .map(([pressName]) => pressName);
  };

  const validate = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Destination name is required';
    } else if (
      editing === 'new' &&
      config?.smbDestinations &&
      formData.name.trim() in config.smbDestinations
    ) {
      newErrors.name = 'A destination with this name already exists';
    }

    if (!formData.uncPath.trim()) {
      newErrors.uncPath = 'UNC path is required';
    }

    if (!formData.stagingPath.trim()) {
      newErrors.stagingPath = 'Staging path is required';
    } else if (!/^(\\\\|\/\/)/.test(formData.stagingPath.trim())) {
      newErrors.stagingPath = 'Staging path must be a UNC path (e.g. \\\\server\\share\\path)';
    }

    if (!formData.hotfolderPath.trim()) {
      newErrors.hotfolderPath = 'Hotfolder path is required';
    } else if (!/^(\\\\|\/\/)/.test(formData.hotfolderPath.trim())) {
      newErrors.hotfolderPath = 'Hotfolder path must be a UNC path (e.g. \\\\server\\share\\path)';
    }

    if (!formData.credentialSetName) {
      newErrors.credentialSetName = 'Credential set is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddNew = () => {
    setEditing('new');
    setFormData({ ...EMPTY_FORM });
    setErrors({});
  };

  const handleEdit = (key: string) => {
    const dest = config?.smbDestinations[key];
    if (!dest) return;
    setEditing(key);
    setFormData({
      name: dest.name || key,
      uncPath: dest.uncPath,
      stagingPath: dest.stagingPath || '',
      hotfolderPath: dest.hotfolderPath || '',
      credentialSetName: dest.credentialSetName,
    });
    setErrors({});
  };

  const handleCancel = () => {
    setEditing(null);
    setFormData({ ...EMPTY_FORM });
    setErrors({});
  };

  const handleSave = async () => {
    if (!validate() || !config) return;

    setSaving(true);
    try {
      const updatedDestinations = { ...config.smbDestinations };

      // If editing and name changed, remove old key
      if (editing !== 'new' && editing !== formData.name.trim()) {
        delete updatedDestinations[editing!];
      }

      updatedDestinations[formData.name.trim()] = {
        name: formData.name.trim(),
        uncPath: formData.uncPath.trim(),
        stagingPath: formData.stagingPath.trim(),
        hotfolderPath: formData.hotfolderPath.trim(),
        credentialSetName: formData.credentialSetName,
      };

      await onSave({
        ...config,
        smbDestinations: updatedDestinations,
      });

      setEditing(null);
      setFormData({ ...EMPTY_FORM });
      setErrors({});
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key: string) => {
    if (!config) return;

    const updatedDestinations = { ...config.smbDestinations };
    delete updatedDestinations[key];

    setSaving(true);
    try {
      await onSave({
        ...config,
        smbDestinations: updatedDestinations,
      });
      setDeleteConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (key: string) => {
    const dest = config?.smbDestinations[key];
    if (!dest) return;

    setTestStates((prev) => ({
      ...prev,
      [key]: { loading: true, result: null },
    }));

    try {
      const result = await testSMBConnection({
        uncPath: dest.uncPath,
        credentialSetName: dest.credentialSetName,
      });
      setTestStates((prev) => ({
        ...prev,
        [key]: { loading: false, result },
      }));
    } catch (err: any) {
      setTestStates((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          result: { success: false, message: err.message || 'Connection test failed' },
        },
      }));
    }
  };

  if (loading && !config) {
    return <div className="text-gray-500">Loading configuration...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with Add New button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">SMB Destinations</h3>
        {editing === null && (
          <button
            type="button"
            onClick={handleAddNew}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Add New
          </button>
        )}
      </div>

      {/* Add New Form (only for new destinations — edits are inline in the table) */}
      {editing === 'new' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Add New SMB Destination</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="dest-name" className="block text-sm font-medium text-gray-700">Destination Name</label>
              <input id="dest-name" type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="CLE-Primary" />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="dest-unc-path" className="block text-sm font-medium text-gray-700">UNC Path (for connection test)</label>
              <input id="dest-unc-path" type="text" value={formData.uncPath} onChange={(e) => setFormData({ ...formData, uncPath: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.uncPath ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} placeholder={"\\\\10.2.28.26\\job_submission"} />
              {errors.uncPath && <p className="mt-1 text-sm text-red-600">{errors.uncPath}</p>}
            </div>
            <div>
              <label htmlFor="dest-staging-path" className="block text-sm font-medium text-gray-700">Staging Path (full UNC)</label>
              <input id="dest-staging-path" type="text" value={formData.stagingPath} onChange={(e) => setFormData({ ...formData, stagingPath: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.stagingPath ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} placeholder={"\\\\10.2.28.26\\job_submission\\hotfolders\\_STAGING"} />
              {errors.stagingPath && <p className="mt-1 text-sm text-red-600">{errors.stagingPath}</p>}
            </div>
            <div>
              <label htmlFor="dest-hotfolder-path" className="block text-sm font-medium text-gray-700">Hotfolder Path (full UNC)</label>
              <input id="dest-hotfolder-path" type="text" value={formData.hotfolderPath} onChange={(e) => setFormData({ ...formData, hotfolderPath: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.hotfolderPath ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} placeholder={"\\\\10.2.28.26\\job_submission\\hotfolders"} />
              {errors.hotfolderPath && <p className="mt-1 text-sm text-red-600">{errors.hotfolderPath}</p>}
            </div>
            <div>
              <label htmlFor="dest-credential-set" className="block text-sm font-medium text-gray-700">Credential Set</label>
              <select id="dest-credential-set" value={formData.credentialSetName} onChange={(e) => setFormData({ ...formData, credentialSetName: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.credentialSetName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}>
                <option value="">Select a credential set...</option>
                {credentialSetNames.map((name) => (<option key={name} value={name}>{name}</option>))}
              </select>
              {errors.credentialSetName && <p className="mt-1 text-sm text-red-600">{errors.credentialSetName}</p>}
            </div>
          </div>
          <div className="mt-4 flex space-x-3">
            <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={handleCancel} disabled={saving} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          </div>
        </div>
      )}

      {/* Destinations List */}
      {destinations.length === 0 && editing === null ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No SMB destinations configured. Click "Add New" to create one.
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hotfolder Path
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credential Set
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {destinations.map(([key, dest]) => {
                const testState = testStates[key];
                const pressesUsing = getPressesUsingDestination(key);
                const isDeleteBlocked = pressesUsing.length > 0;

                return (
                  <React.Fragment key={key}>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {dest.name || key}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {dest.hotfolderPath || dest.uncPath}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {dest.credentialSetName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(key)}
                          disabled={editing !== null}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(key)}
                          disabled={editing !== null}
                          className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          onClick={() => handleTest(key)}
                          disabled={editing !== null || testState?.loading}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {testState?.loading ? 'Testing...' : 'Test'}
                        </button>
                      </td>
                    </tr>
                    {/* Inline edit form */}
                    {editing === key && (
                      <tr>
                        <td colSpan={4} className="px-6 py-4 bg-blue-50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">UNC Path (for connection test)</label>
                              <input type="text" value={formData.uncPath} onChange={(e) => setFormData({ ...formData, uncPath: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.uncPath ? 'border-red-300' : 'border-gray-300'}`} placeholder={"\\\\10.2.28.26\\job_submission"} />
                              {errors.uncPath && <p className="mt-1 text-sm text-red-600">{errors.uncPath}</p>}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Staging Path (full UNC)</label>
                              <input type="text" value={formData.stagingPath} onChange={(e) => setFormData({ ...formData, stagingPath: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.stagingPath ? 'border-red-300' : 'border-gray-300'}`} placeholder={"\\\\10.2.28.26\\job_submission\\hotfolders\\_STAGING"} />
                              {errors.stagingPath && <p className="mt-1 text-sm text-red-600">{errors.stagingPath}</p>}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Hotfolder Path (full UNC)</label>
                              <input type="text" value={formData.hotfolderPath} onChange={(e) => setFormData({ ...formData, hotfolderPath: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.hotfolderPath ? 'border-red-300' : 'border-gray-300'}`} placeholder={"\\\\10.2.28.26\\job_submission\\hotfolders"} />
                              {errors.hotfolderPath && <p className="mt-1 text-sm text-red-600">{errors.hotfolderPath}</p>}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Credential Set</label>
                              <select value={formData.credentialSetName} onChange={(e) => setFormData({ ...formData, credentialSetName: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.credentialSetName ? 'border-red-300' : 'border-gray-300'}`}>
                                <option value="">Select a credential set...</option>
                                {credentialSetNames.map((n) => (<option key={n} value={n}>{n}</option>))}
                              </select>
                              {errors.credentialSetName && <p className="mt-1 text-sm text-red-600">{errors.credentialSetName}</p>}
                            </div>
                          </div>
                          <div className="mt-3 flex space-x-3">
                            <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">{saving ? 'Saving...' : 'Save'}</button>
                            <button type="button" onClick={handleCancel} disabled={saving} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Test result banner */}
                    {testState?.result && (
                      <tr>
                        <td colSpan={4} className="px-6 py-0 pb-3">
                          <TestResultBanner result={testState.result} />
                        </td>
                      </tr>
                    )}
                    {/* Delete confirmation / blocked message */}
                    {deleteConfirm === key && (
                      <tr>
                        <td colSpan={4} className="px-6 py-3">
                          {isDeleteBlocked ? (
                            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md" role="alert">
                              <p className="text-sm font-medium">Cannot delete this destination</p>
                              <p className="text-sm mt-1">
                                This destination is currently assigned to the following presses:{' '}
                                <span className="font-medium">{pressesUsing.join(', ')}</span>.
                                Please reassign these presses before deleting.
                              </p>
                              <button
                                type="button"
                                onClick={() => setDeleteConfirm(null)}
                                className="mt-2 text-sm font-medium text-yellow-800 underline hover:text-yellow-900"
                              >
                                Dismiss
                              </button>
                            </div>
                          ) : (
                            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md" role="alert">
                              <p className="text-sm">
                                Are you sure you want to delete <span className="font-medium">{dest.name || key}</span>?
                              </p>
                              <div className="mt-2 flex space-x-3">
                                <button
                                  type="button"
                                  onClick={() => handleDelete(key)}
                                  disabled={saving}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                >
                                  {saving ? 'Deleting...' : 'Confirm Delete'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeleteConfirm(null)}
                                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/** Small helper component for test result banners */
const TestResultBanner: React.FC<{ result: TestResult }> = ({ result }) => (
  <div
    className={`p-3 rounded-md flex items-center space-x-2 ${
      result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
    }`}
    role="alert"
  >
    <span className="text-lg">{result.success ? '✓' : '✗'}</span>
    <span className="text-sm">{result.message || (result.success ? 'Connection successful' : 'Connection failed')}</span>
  </div>
);

export default SMBDestinationsTab;
